/* VDN homepage videos: native scrolling, progressive media and theme editor support. */
(() => {
  if (customElements.get('vdnx-carousel')) return;

  class VdnxCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.vx-carousel-track');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('.vx-carousel-slide'));
      this.navigation = this.querySelector('.vx-carousel-nav');
      this.previous = this.querySelector('[data-vx-prev]');
      this.next = this.querySelector('[data-vx-next]');
      this.counter = this.querySelector('[data-vx-count]');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', this.schedule, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          this.scrollToPosition(event.key === 'Home' ? 0 : this.maximum());
        } else {
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.addEventListener('play', event => {
        if (event.target.tagName !== 'VIDEO') return;
        this.querySelectorAll('video').forEach(video => {
          if (video !== event.target && !video.paused) video.pause();
        });
      }, { ...options, capture: true });
      this.addEventListener('focusin', event => {
        const slide = event.target.closest('.vx-carousel-slide');
        if (!slide) return;
        const bounds = this.track.getBoundingClientRect();
        const card = slide.getBoundingClientRect();
        if (card.left < bounds.left - 2 || card.right > bounds.right + 2) {
          this.reveal(slide);
        }
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const slide = this.slides.find(item => item.dataset.blockId === event.detail?.blockId)
          || event.target.closest('.vx-carousel-slide');
        if (slide) this.reveal(slide);
      }, options);
      document.addEventListener('visibilitychange', this.schedule, options);

      this.nearViewport = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver(entries => {
          this.nearViewport = entries[0].isIntersecting;
          this.schedule();
        }, { rootMargin: '200px 0px' });
        this.viewportObserver.observe(this.track);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.schedule, options);
      }
      this.update();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.viewportObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.slides?.forEach(slide => this.pauseMedia(slide));
    }

    maximum() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    position(slide) {
      return slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    }

    scrollToPosition(left, immediate = false) {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: Math.min(this.maximum(), Math.max(0, left)),
        behavior: immediate || reducedMotion ? 'auto' : 'smooth'
      });
    }

    move(direction) {
      const current = this.track.scrollLeft;
      const positions = this.slides.map(slide => Math.min(this.maximum(), this.position(slide)));
      const target = direction > 0
        ? positions.find(position => position > current + 2)
        : positions.reverse().find(position => position < current - 2);
      this.scrollToPosition(target ?? (direction > 0 ? this.maximum() : 0));
    }

    reveal(slide) {
      this.loadMedia(slide);
      this.scrollToPosition(this.position(slide), true);
      this.schedule();
    }

    loadMedia(slide) {
      const template = slide.querySelector('template[data-vx-media]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      slide.querySelectorAll('iframe[data-vx-suspended-src]').forEach(frame => {
        frame.src = frame.dataset.vxSuspendedSrc;
        delete frame.dataset.vxSuspendedSrc;
      });
    }

    pauseMedia(slide) {
      slide.querySelectorAll('video').forEach(video => {
        if (!video.paused) video.pause();
      });
      // Removing the embed stops offscreen audio without third-party player SDKs.
      // The original URL (including a private Vimeo hash) is restored on return.
      slide.querySelectorAll('iframe[src]').forEach(frame => {
        frame.dataset.vxSuspendedSrc = frame.getAttribute('src');
        frame.removeAttribute('src');
      });
    }

    update() {
      const maximum = this.maximum();
      const current = Math.min(maximum, Math.max(0, this.track.scrollLeft));
      this.navigation.hidden = maximum < 3;
      this.previous.disabled = current < 3;
      this.next.disabled = current > maximum - 3;
      const viewport = this.track.getBoundingClientRect();
      const visible = [];
      this.slides.forEach((slide, index) => {
        const bounds = slide.getBoundingClientRect();
        const inView = Math.min(bounds.right, viewport.right) - Math.max(bounds.left, viewport.left) > 2;
        if (inView) visible.push(index + 1);
        if (inView && this.nearViewport && !document.hidden) {
          this.loadMedia(slide);
        } else {
          this.pauseMedia(slide);
        }
      });
      const first = visible[0] || 0;
      const last = visible[visible.length - 1] || first;
      const label = `${first === last ? first : `${first}–${last}`} de ${this.slides.length}`;
      if (this.counter.textContent !== label) this.counter.textContent = label;
    }
  }

  customElements.define('vdnx-carousel', VdnxCarousel);
})();
